import { useEffect, useReducer, useState } from "react";

import DomainIcon from '@material-ui/icons/Domain';
import { toast } from "react-toastify";
import {
  Chip,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { useDebounce } from 'use-debounce';

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableManager from "../../components/TableManager/TableManager";
import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";

import { socketConnection } from "../../services/socket";
import api, { coreApi } from "../../services/api";

import TrashIcon from "../../assets/icon/TrashIcon";
import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import AddIcon from "../../assets/icon/AddIcon";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableContainer: {
    width: '100%',
    maxWidth: '80%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '90%'
    },
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_QUEUES":
      return Array.isArray(action.payload) ? [...action.payload] : state;
    case "UPDATE_QUEUES":
      const queueIndex = state.findIndex(queue => queue.id === action.payload.id);
      if (queueIndex !== -1) {
        return state.map(queue => queue.id === action.payload.id ? action.payload : queue);
      } else {
        return [action.payload, ...state];
      }
    case "DELETE_QUEUE":
      return state.filter(queue => queue.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Queues = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const classes = useStyles();
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearch] = useDebounce(searchParam, 500);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false)
  const deleteMutation = coreApi.queue.useDelete();
  const { data, isLoading, error } = coreApi.queue.useList({
    searchParam: debouncedSearch,
    pageNumber
  });

  console.log('data', data)

  useEffect(() => {
    setPageNumber(1);
  }, [searchParam]);

  const handleOpenQueueModal = () => {
    setSelectedQueue(null);
    setQueueModalOpen(true);
  };

  const handleCloseQueueModal = () => {
    setSelectedQueue(null);
    setQueueModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleDeleteQueue = async (queueId) => {
    setIsLoadingDelete(true);
    try {
      await deleteMutation.mutateAsync({ id: queueId });
      toast.success(i18n.t("queues.toast.delete"));
    } catch (err) {
      toastError(err);
    } finally {
      setIsLoadingDelete(false);
      setSelectedQueue(null);
      setConfirmModalOpen(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const primaryButtons = [
    { label: i18n.t("queues.buttons.add"), onClick: handleOpenQueueModal, icon: <AddIcon fill='#000' /> },
  ];

  const columns = [
    {
      field: 'name',
      headerName: i18n.t("queues.table.name"),
      headerStyle: { fontWeight: '400' },
      width: '28%',
      renderCell: ({ row }) => (
        <Chip
          icon={<DomainIcon style={{ color: row.color }}/>}
          variant="outlined"
          style={{
            border: `2px solid ${row.color}`
          }}
          label={row.name}
        />
      ),
    },
    {
      field: 'greetingMessage',
      headerName: i18n.t("queues.table.greeting"),
      renderCell: ({ row }) => (
        <div style={{ width: 'auto', textAlign: "left", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row.greetingMessage}
        </div>
      ),
      headerStyle: { fontWeight: '400' }, width: '56%'
    },
  ];

  const actionButtons = [
    ({ row }) => (
      <IconButton size="small" onClick={() => handleEditQueue(row)}>
        <PencilEditIcon fill="#7F7F7F" />
      </IconButton>
    ),
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true);
          setSelectedQueue(row);
        }}
      >
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
    ),
  ];

  return (
    <>
      <div className={classes.mainContainer} >
        <ConfirmationModal
          title={selectedQueue && `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => handleDeleteQueue(selectedQueue.id)}
          isLoadingConfirmation={isLoadingDelete}
          onCloseAfterConfirming={false}
        >
          {i18n.t("queues.confirmationModal.deleteMessage")}
        </ConfirmationModal>
        <QueueModal
          open={queueModalOpen}
          onClose={handleCloseQueueModal}
          queueId={selectedQueue?.id}
          onQueueCreated={() => {
            setQueueModalOpen(false);
          }}
        />
        <div className={classes.tableContainer}>
          <HeaderManager
            title={i18n.t("queues.title")}
            primaryButtons={primaryButtons}
            onSearch={handleSearch}
          />
          <TableManager
            columns={columns}
            data={data ?? []}
            loading={isLoading}
            onScroll={loadMore}
            actionButtons={actionButtons}
          />
        </div>
      </div>
    </>
  );
};

export default Queues;
